import {
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  CrudMode,
  Dialog,
  handleClose,
  Page,
  PageRoute,
  ScreenType,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import {
  Permission,
  StudentTextingOptInEditResource,
  StudentTextingOptInViewResource,
  TextingOptIn,
} from "generated/openApi";
import "moment-timezone";
import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { CollegeContext, collegePageRoute } from "pages/system/utility/colleges/CollegePage/CollegePage";
import { useRouter } from "hooks";
import { StudentTextingOptInPageHeader } from "./StudentTextingOptinPageHeader";
import { StudentTextingOptInPageBody } from "./StudentTextingOptinPageBody";

const routeName = "studentTextingOptin";

class Params {
  id: number = 0;
  studentId: number = 0;
  crudMode: CrudMode = "view";
  screenType: ScreenType = "standard";
}

export const studentTextingOptinPageRoute: PageRoute<Params> = {
  handle: "Texting Opt-In",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
};

export const StudentTextingOptinPage = () => {
  const ROOT_PERMISSION: Permission = Permission.Texting;
  const { params } = useParams(routeName, Params);
  const navigate = useNavigate();
  const { matchTemplatePath } = useRouter();

  const [textingOptIn, setTextingOptIn] = useState<TextingOptIn>({} as TextingOptIn);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [textingOptIn] });
  const [viewResource, setViewResource] = useState<StudentTextingOptInViewResource>({});
  const [editResource, setEditResource] = useState<StudentTextingOptInEditResource>({});
  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    fetch(params.studentId);
    fetchViewResource();
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit") fetchEditResource();
  }, [params.crudMode]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(
      `../${studentTextingOptinPageRoute.getPath({
        id: id,
        studentId: params.studentId,
        crudMode: crudMode,
        screenType: params.screenType,
      })}`
    );

  const fetch = (studentId: number) => {
    ApiService.textingOptIn.getByStudentId(studentId).then((response) => {
      setTextingOptIn(response);
      resetUnsavedChanges([response]);
    });
  };

  const fetchViewResource = () => {
    ApiService.textingOptIn.getViewResource().then((response) => {
      setViewResource(response);
    });
  };

  const fetchEditResource = () => {
    ApiService.textingOptIn.getEditResource().then((response) => {
      setEditResource(response);
    });
  };

  const pageHeader = (
    <StudentTextingOptInPageHeader
      rootPermission={ROOT_PERMISSION}
      handle={studentTextingOptinPageRoute.handle}
      crudMode={params.crudMode}
      reroute={reroute}
      fetch={fetch}
      textingOptIn={textingOptIn}
      setValidationErrors={setValidationErrors}
      unsavedChanges={unsavedChanges}
    />
  );

  const pageBody = (
    <StudentTextingOptInPageBody
      crudMode={params.crudMode}
      textingOptIn={textingOptIn}
      setTextingOptIn={setTextingOptIn}
      viewResource={viewResource}
      editResource={editResource}
      validationErrors={validationErrors}
    />
  );

  return (
    <>
      {params.screenType === "standard" ? (
        <Page isForm header={pageHeader}>
          {pageBody}
        </Page>
      ) : (
        <Dialog
          isForm
          open={true}
          onClose={() => handleClose(navigate, unsavedChanges)}
          maxWidth="sm"
          header={pageHeader}
          content={pageBody}
        />
      )}

      {matchTemplatePath(collegePageRoute.templatePath) && <Outlet />}
    </>
  );
};
